import MUIDataTable from "mui-datatables";
import React from "react";
import { observer } from "mobx-react";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { Typography } from "@mui/material";
import buildTimeOverviewTableModel from "../utils/timeOverviewTableModel";

const SimpleTable = ({
  rawData = null,
  data = {},
  nls = {},
  order,
  skip = [],
  tableResult = null,
  useNewLayout = false,
}) => {
  console.log("Rendering SimpleTable...", { data, tableResult });
  const getMuiTheme = () =>
    createTheme({
      overrides: {
        MuiTableCell: {
          head: {
            fontWeight: 600,
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 0,
          },
          root: {
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 0,
          },
        },

        MUIDataTableBodyRow: {
          root: {
            "&:nth-child(odd)": {
              backgroundColor: "#CCCCCC",
            },
            "&:nth-child(even)": {
              backgroundColor: "#EEEEEE",
            },
          },
        },
      },
    });

  // Render custom table layout for the new format
  if (useNewLayout && tableResult) {
    const model = buildTimeOverviewTableModel(tableResult, nls);
    const {
      beobachtungsdaten = "Beobachtungsdaten",
      projektionsdaten = "Projektionsdaten",
      rcp26 = "RCP2.6",
      rcp85 = "RCP8.5",
    } = nls;

    const { observations, projectionRows } = model;
    const hasObservations = Array.isArray(observations) && observations.length;
    const hasProjections =
      Array.isArray(projectionRows) && projectionRows.length;

    return (
      <Paper elevation={0} style={{ paddingTop: 10 }}>
        <Table
          size="small"
          style={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <colgroup>
            <col style={{ width: "86px", minWidth: "86px" }} />
            <col style={{ width: "calc((100% - 86px) / 2)" }} />
            <col style={{ width: "calc((100% - 86px) / 2)" }} />
          </colgroup>
          <TableBody>
            {hasObservations ? (
              <>
                {/* Beobachtungsdaten Section Header */}
                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{
                      backgroundColor: "rgba(211, 211, 211, 1)",
                      fontWeight: 600,
                      textAlign: "center",
                      padding: "8px",
                      border: "1px solid #999",
                    }}
                  >
                    {beobachtungsdaten}
                  </TableCell>
                </TableRow>

                {/* Beobachtungsdaten Rows - 2 columns spanning full width */}
                {observations.map((obs, index) => (
                  <TableRow key={`obs-${index}`}>
                    <TableCell
                      style={{
                        padding: "5px 8px",
                        border: "1px solid #999",
                        width: "86px",
                        minWidth: "86px",
                      }}
                      title={obs.period}
                    >
                      {obs.period}
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      style={{
                        padding: "5px 8px",
                        textAlign: "center",
                        border: "1px solid #999",
                        width: "calc(100% - 86px)",
                      }}
                    >
                      {obs.value}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : null}

            {hasProjections ? (
              <>
                {/* Projektionsdaten Section Header */}
                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{
                      backgroundColor: "#D3D3D3",
                      fontWeight: 600,
                      textAlign: "center",
                      padding: "8px",
                      border: "1px solid #999",
                    }}
                  >
                    {projektionsdaten}
                  </TableCell>
                </TableRow>

                {/* Projektionsdaten Column Headers */}
                <TableRow>
                  <TableCell
                    style={{
                      fontWeight: 400,
                      padding: "5px 8px",
                      border: "1px solid #999",
                      width: "86px",
                      minWidth: "86px",
                    }}
                  >
                    {/* Empty header for period column */}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: "#A8D5FF",
                      fontWeight: 500,
                      textAlign: "center",
                      padding: "5px 8px",
                      border: "1px solid #999",
                      width: "calc((100% - 86px) / 2)",
                    }}
                  >
                    {rcp26}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: "#FFB3B3",
                      fontWeight: 500,
                      textAlign: "center",
                      padding: "5px 8px",
                      border: "1px solid #999",
                      width: "calc((100% - 86px) / 2)",
                    }}
                  >
                    {rcp85}
                  </TableCell>
                </TableRow>

                {/* Projektionsdaten Rows - all 3 columns used */}
                {projectionRows.map((proj, index) => (
                  <TableRow key={`proj-${index}`}>
                    <TableCell
                      style={{
                        padding: "5px 8px",
                        border: "1px solid #999",
                        width: "86px",
                        minWidth: "86px",
                      }}
                      title={proj.period}
                    >
                      {proj.period}
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "5px 8px",
                        textAlign: "center",
                        border: "1px solid #999",
                        backgroundColor: "#D4EAFF",
                        width: "calc((100% - 86px) / 2)",
                      }}
                    >
                      {proj.rcp26}
                    </TableCell>
                    <TableCell
                      style={{
                        padding: "5px 8px",
                        textAlign: "center",
                        border: "1px solid #999",
                        backgroundColor: "#FFD9D9",
                        width: "calc((100% - 86px) / 2)",
                      }}
                    >
                      {proj.rcp85}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  // Original table layout
  const getTableColumns = () => {
    const { attribute = "Kennzahl", value = "Wert" } = nls;
    return [
      {
        name: attribute,
        label: attribute,
        options: {
          customBodyRender: (value, tableMeta, updateValue) => {
            return value && value.tooltip ? (
              <Tooltip title={value.tooltip}>
                <span>{value.value}</span>
              </Tooltip>
            ) : (
              <span>{value}</span>
            );
          },
        },
      },
      {
        name: value,
        label: value,
        options: {
          customBodyRender: (value, tableMeta, updateValue) => {
            return value && value.tooltip ? (
              <Tooltip title={value.tooltip}>
                <span>{value.value}</span>
              </Tooltip>
            ) : (
              <span>{value}</span>
            );
          },
        },
      },
    ];
  };

  const getTableRows = () => {
    if (rawData) {
      return rawData;
    } else {
      const colOrder = order || Object.keys(data);
      return colOrder
        .map((key) => {
          return skip.indexOf(key) !== -1 ||
            !(typeof data[key] in { string: 1, boolean: 1, number: 1 })
            ? false
            : [nls[key] || key, data[key]];
        })
        .filter((obj) => !!obj);
    }
  };

  const { statistischeKennzahlen = "Statistische Kennzahlen" } = nls;
  return (
    //nodata
    <ThemeProvider theme={getMuiTheme()}>
      {statistischeKennzahlen && (
        <Box style={{ paddingTop: 10, paddingBottom: 10 }}>
          <Typography variant="strong">{statistischeKennzahlen}</Typography>
        </Box>
      )}
      <MUIDataTable
        // data={data}
        options={{
          filter: false,
          pagination: false,
          sort: false,
          search: false,
          download: false,
          draggable: false,
          print: false,
          viewColumns: false,
          selectableRows: "none",
          rowHover: false,
          elevation: 0,
        }}
        data={getTableRows()}
        columns={getTableColumns()}
      />
    </ThemeProvider>
  );
};

export default observer(SimpleTable);
